import { z } from 'zod'
import {
  EntitledCatalogueEntityType,
  EntitledCatalogueSelectionMode,
  PrerequisiteCatalogueEntityType,
  PrerequisiteType,
  CustomerEligibilityV2Type,
  DiscountStackMode
} from 'src/lib/types/discountsV2'

export const requiredString = z.string().trim().min(1, 'Required')
export const positiveNumber = z.number().positive('Must be greater than 0')
export const nonNegativeNumber = z.number().min(0, 'Must be 0 or greater')
export const nonNegativeNumbersMinusOne = nonNegativeNumber.or(z.literal(-1))

export const refineCatalogueDuplicates = (
  catalogue: { entities: { entityId: string[] }[] }[],
  ctx: z.RefinementCtx,
  pathPrefix: string = 'prerequisiteCatalogue'
) => {
  if (!catalogue) return

  const seenIds = new Map<string, { groupIndex: number; entityIndex: number }>()

  catalogue.forEach((group, groupIndex) => {
    if (!group?.entities) return
    group.entities.forEach((entity, entityIndex) => {
      if (!entity?.entityId) return
      entity.entityId.forEach(id => {
        if (seenIds.has(id)) {
          ctx.addIssue({
            code: 'custom',
            message: 'Duplicate product selected',
            path: [pathPrefix, groupIndex, 'entities', entityIndex]
          })
        } else {
          seenIds.set(id, { groupIndex, entityIndex })
        }
      })
    })
  })
}

export const CatalogueEntitySchema = z.object({
  type: z.enum([
    EntitledCatalogueEntityType.PRODUCT,
    EntitledCatalogueEntityType.COLLECTION,
    EntitledCatalogueEntityType.VARIANT,
    EntitledCatalogueEntityType.PRODUCT_AND_VARIANT
  ]),
  entityId: z.array(z.string())
})

export const EntitledCatalogueGroupSchema = z
  .object({
    quantity: z.number().optional().nullable(),
    entities: z.array(CatalogueEntitySchema),
    selectionMode: z.enum([EntitledCatalogueSelectionMode.ALL, EntitledCatalogueSelectionMode.ANY])
  })
  .superRefine((data, ctx) => {
    if (data.selectionMode === EntitledCatalogueSelectionMode.ANY) {
      if (!data.quantity || data.quantity <= 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Quantity must be at least 1',
          path: ['quantity']
        })
      }
    }
  })

export const EntitledCatalogueSchema = z.array(EntitledCatalogueGroupSchema)

export const PrerequisiteCatalogueEntitySchema = z.object({
  type: z.enum([
    PrerequisiteCatalogueEntityType.PRODUCT,
    PrerequisiteCatalogueEntityType.COLLECTION,
    PrerequisiteCatalogueEntityType.VARIANT
  ]),
  entityId: z.array(z.string())
})

export const PrerequisiteCatalogueGroupSchema = z
  .object({
    type: z.enum([PrerequisiteType.PRICE, PrerequisiteType.QUANTITY]),
    quantity: z.number().optional().nullable(),
    price: z.number().optional().nullable(),
    maxQuantity: z.number().optional().nullable(),
    maxPrice: z.number().optional().nullable(),
    entities: z.array(PrerequisiteCatalogueEntitySchema)
  })
  .superRefine((data, ctx) => {
    if (data.type === PrerequisiteType.PRICE && data.maxPrice != null && data.price != null) {
      if (data.maxPrice <= data.price) {
        ctx.addIssue({
          code: 'custom',
          message: 'Max limit cannot be less than or equal to min limit',
          path: ['maxPrice']
        })
      }
    }

    if (data.type === PrerequisiteType.QUANTITY && data.maxQuantity != null && data.quantity != null) {
      if (data.maxQuantity < data.quantity) {
        ctx.addIssue({
          code: 'custom',
          message: 'Max limit cannot be less than min limit',
          path: ['maxQuantity']
        })
      }
    }
  })

export const PrerequisiteCatalogueSchema = z.array(PrerequisiteCatalogueGroupSchema)

export const CustomerEligibilitySchema = z
  .object({
    type: z.enum([
      CustomerEligibilityV2Type.ALL_CUSTOMERS,
      CustomerEligibilityV2Type.FIRST_TIME_CUSTOMERS,
      CustomerEligibilityV2Type.LIFETIME_ORDER_COUNT,
      CustomerEligibilityV2Type.SPECIFIC_CUSTOMERS,
      CustomerEligibilityV2Type.CUSTOMER_SPECIFIC,
      CustomerEligibilityV2Type.UTM_PARAMS
    ]),
    segmentIds: z.array(z.string()).optional(),
    fileMeta: z
      .object({
        fileUrl: z.string().nullable().optional(),
        fileKey: z.string().nullable().optional(),
        downloadUrl: z.string().nullable().optional(),
        fileName: z.string().nullable().optional(),
        size: z.number().nullable().optional()
      })
      .optional(),
    prerequisiteUserOrderCohort: z
      .object({
        lifetimeMinOrderCount: z.number().nullable().optional(),
        lifetimeMaxOrderCount: z.number().nullable().optional()
      })
      .optional(),
    utmParams: z
      .object({
        source: z.array(z.string()),
        medium: z.array(z.string()),
        campaign: z.array(z.string()),
        content: z.array(z.string())
      })
      .optional()
  })
  .superRefine((data, ctx) => {
    if (data.type === CustomerEligibilityV2Type.LIFETIME_ORDER_COUNT) {
      if (
        data.prerequisiteUserOrderCohort?.lifetimeMinOrderCount === undefined ||
        data.prerequisiteUserOrderCohort?.lifetimeMinOrderCount === null
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Minimum order count is required',
          path: ['prerequisiteUserOrderCohort', 'lifetimeMinOrderCount']
        })
      }

      const min = data.prerequisiteUserOrderCohort?.lifetimeMinOrderCount
      const max = data.prerequisiteUserOrderCohort?.lifetimeMaxOrderCount

      if (min != null && max != null && max <= min) {
        ctx.addIssue({
          code: 'custom',
          message: 'Max limit cannot be less than or equal to min limit',
          path: ['prerequisiteUserOrderCohort', 'lifetimeMaxOrderCount']
        })
      }
    }
    if (data.type === CustomerEligibilityV2Type.SPECIFIC_CUSTOMERS) {
      if (!data.fileMeta?.fileUrl && !data.fileMeta?.downloadUrl) {
        ctx.addIssue({
          code: 'custom',
          message: 'File upload is required',
          path: ['fileMeta']
        })
      }
    }
    if (data.type === CustomerEligibilityV2Type.CUSTOMER_SPECIFIC) {
      if (!data.segmentIds || data.segmentIds.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Select at least one segment',
          path: ['segmentIds']
        })
      }
    }
    if (data.type === CustomerEligibilityV2Type.UTM_PARAMS) {
      const hasUtms = Object.values(data.utmParams || {}).some(arr => arr && arr.length > 0)
      if (!hasUtms) {
        ctx.addIssue({
          code: 'custom',
          message: 'Add at least one UTM parameter',
          path: ['utmParams']
        })
      }
    }
  })

export const StackabilitySchema = z
  .object({
    stackMode: z.enum([DiscountStackMode.ALL, DiscountStackMode.NONE, DiscountStackMode.CUSTOM]),
    customStackableDiscountIds: z.array(z.string()).optional()
  })
  .superRefine((data, ctx) => {
    if (data.stackMode === DiscountStackMode.CUSTOM) {
      if (!data.customStackableDiscountIds || data.customStackableDiscountIds.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Select at least one discount',
          path: ['customStackableDiscountIds']
        })
      }
    }
  })
